/**
 * The rules a weapon plays by, as opposed to the numbers it has.
 *
 * Four hundred models with a damage figure and a rate of fire is fifteen guns
 * wearing four hundred coats. You can feel the difference between a shotgun
 * and a rifle, and between nothing else — two SMGs eleven points apart are the
 * same gun, and the fourth time the run hands you one you stop reading the
 * name.
 *
 * What actually separates weapons in the games that get this right is not the
 * arithmetic, it is that each family answers a different question. Not "how
 * much damage" but *when do I pull*, *where do I stand*, *do I move while I
 * shoot*, *what do I shoot first*, *when do I reload*. So every action in the
 * library carries one rule, free and always on, chosen so that the answer to
 * one of those questions changes when you pick a different gun up.
 *
 * Intrinsic rules are keyed off the *action* rather than the class, because
 * the action is the thing the player can see: the view model works a bolt, a
 * pump, a lever, and the rule is attached to the animation they are already
 * watching. A pump shotgun and a pump grenade launcher behave alike, and they
 * should — that is what having a pump means.
 *
 * The cards on top of these are the rule *breakers*, and they are deliberately
 * absurd, because a run people talk about afterwards is one where something
 * happened that should not have. "Every third shell is explosive" is a memory;
 * "+12% assault rifle damage" is not.
 */

import type { WeaponAction } from './weapons.ts';

export type Trait =
  // --- intrinsic, one per action -------------------------------------------
  /** A shot taken a beat after the last one hits much harder. */
  | 'deliberate'
  /** Held fire winds up to a faster rate, and drops the moment you let go. */
  | 'spinup'
  /** The last round of a burst always lands as a hit to the head. */
  | 'last_round'
  /** Hits interrupt: the target is slowed and its next attack is delayed. */
  | 'stagger'
  /** A hit to the head works the bolt for you. */
  | 'chamber'
  /** Working the action while walking into the fight carries you forward. */
  | 'canter'
  /** The first shot after a pause carries the whole chamber. */
  | 'overpressure'
  /** Point blank throws them, and you. */
  | 'blast'

  // --- earned, and they break something ------------------------------------
  /** Pumps fire while the trigger is held, and the cone pays for it. */
  | 'slamfire'
  /** Every third round in the magazine goes off. */
  | 'third_shell'
  /** The magazine you drop is a grenade. */
  | 'mag_grenade'
  /** Kills stack damage; a shot that hits nothing at all clears it. */
  | 'battle_rhythm'
  /** Sustained fire cools what it hits until it can barely move. */
  | 'deep_freeze';

export interface TraitNote {
  name: string;
  /** One line, in the player's terms, for the weapon card. */
  text: string;
}

/**
 * What each intrinsic rule is called, so the pickup line can say it.
 *
 * A rule the player is never told about is a rule they attribute to noise.
 * These are short enough to sit under a weapon name without becoming a manual.
 */
export const INTRINSIC: Record<WeaponAction, { trait: Trait } & TraitNote> = {
  semi: {
    trait: 'deliberate',
    name: 'Deliberate',
    text: 'A shot taken a beat after the last one hits far harder.',
  },
  auto: {
    trait: 'spinup',
    name: 'Wound Up',
    text: 'Held fire climbs to a faster rate. Letting go drops it.',
  },
  burst: {
    trait: 'last_round',
    name: 'Third Round',
    text: 'The last round of every burst lands as a hit to the head.',
  },
  pump: {
    trait: 'stagger',
    name: 'Concussive',
    text: 'Hits stagger: slowed, and whatever it was about to do is delayed.',
  },
  bolt: {
    trait: 'chamber',
    name: 'Chambered',
    text: 'A hit to the head works the bolt for you.',
  },
  lever: {
    trait: 'canter',
    name: 'Canter',
    text: 'Cycling while walking forward carries you forward faster.',
  },
  charge: {
    trait: 'overpressure',
    name: 'Overpressure',
    text: 'The first shot after a pause carries the whole chamber.',
  },
  breech: {
    trait: 'blast',
    name: 'Point Blank',
    text: 'Close enough and it throws them — and shoves you back.',
  },
};

/** How long a `deliberate` weapon needs between rounds to earn its bonus. */
export const DELIBERATE_GAP = 0.3;
export const DELIBERATE_BONUS = 1.55;
/** How much rate a wound-up automatic gains, and how long it takes. */
export const SPINUP_GAIN = 0.22;
export const SPINUP_TIME = 1.5;
/** How long a pause an `overpressure` weapon wants, and what it pays. */
export const OVERPRESSURE_GAP = 1.4;
export const OVERPRESSURE_BONUS = 2.2;
/** Inside this range a break-action throws what it hits. */
export const BLAST_RANGE = 5;
export const BLAST_BONUS = 1.7;
/** Per-kill damage a battle rhythm stacks, and the ceiling on it. */
export const RHYTHM_STEP = 0.09;
export const RHYTHM_CAP = 8;
